import React from "react";
import { Link, NavLink, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Rocket,
  LayoutDashboard,
  Play,
  Library,
  ClipboardList,
  Users,
  FolderTree,
  Sparkles,
  Share2,
  LifeBuoy,
  BookOpen,
  HelpCircle,
} from "lucide-react";
import { HELP_SECTIONS, getSectionBySlug } from "@/help/sections.js";

const ICONS = {
  Rocket,
  LayoutDashboard,
  Play,
  Library,
  ClipboardList,
  Users,
  FolderTree,
  Sparkles,
  Share2,
  LifeBuoy,
  BookOpen,
};

function HelpSidebar({ activeSlug }) {
  return (
    <aside
      data-testid="help-sidebar"
      className="sticky top-20 hidden h-fit shrink-0 lg:block lg:w-72"
    >
      <Link
        to="/help"
        data-testid="help-sidebar-home"
        className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
      >
        <HelpCircle size={16} className="text-indigo-600" />
        Help Center
      </Link>
      <nav className="space-y-1">
        {HELP_SECTIONS.map((s) => {
          const Icon = ICONS[s.icon] || HelpCircle;
          const active = s.slug === activeSlug;
          return (
            <NavLink
              key={s.slug}
              to={`/help/${s.slug}`}
              data-testid={`help-nav-${s.slug}`}
              className={[
                "flex items-start gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                active
                  ? "bg-indigo-50 text-indigo-700 font-semibold"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              ].join(" ")}
            >
              <Icon
                size={15}
                className={active ? "mt-0.5 text-indigo-600" : "mt-0.5 text-slate-400"}
              />
              <span className="leading-snug">{s.title}</span>
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

function HelpIndex() {
  return (
    <div data-testid="help-index" className="space-y-6">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Eyvo Demo Agent — Help Center
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Everything you need to know to drive Eyvo demos in under an hour.
          Eleven short sections — start anywhere.
        </p>
      </header>

      <ul className="grid gap-3 sm:grid-cols-2">
        {HELP_SECTIONS.map((s) => {
          const Icon = ICONS[s.icon] || HelpCircle;
          return (
            <li key={s.slug}>
              <Link
                to={`/help/${s.slug}`}
                data-testid={`help-card-${s.slug}`}
                className="block rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-indigo-300 hover:bg-indigo-50/30"
              >
                <div className="flex items-start gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-indigo-50 text-indigo-600">
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">
                      {s.title}
                    </div>
                    <div className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      {s.blurb}
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HelpSection({ section }) {
  return (
    <article
      data-testid={`help-content-${section.slug}`}
      className="prose prose-slate max-w-none prose-headings:tracking-tight prose-h1:text-3xl prose-h1:font-semibold prose-h2:mt-8 prose-h2:text-xl prose-h3:text-base prose-code:rounded prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:font-medium prose-code:before:content-none prose-code:after:content-none prose-pre:bg-slate-900 prose-table:text-sm"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
    </article>
  );
}

export default function HelpPage() {
  const { slug } = useParams();
  const section = slug ? getSectionBySlug(slug) : null;
  if (slug && !section) {
    return <Navigate to="/help" replace />;
  }
  return (
    <div className="flex gap-10">
      <HelpSidebar activeSlug={slug} />
      <main
        data-testid="help-content"
        className="min-w-0 flex-1"
      >
        {section ? <HelpSection section={section} /> : <HelpIndex />}
      </main>
    </div>
  );
}
